import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Avatar
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Build as BuildIcon,
  LocalShipping as DeliveryIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';

const MaintenanceFlowExample: React.FC = () => {
  const exampleData = {
    device: 'لابتوب HP',
    customer: 'أحمد محمد',
    maintenanceFee: 500,
    partsRevenue: 200,
    totalCost: 700,
    timeline: [
      {
        date: '2024-01-20',
        action: 'استقبال الجهاز',
        status: 'received',
        icon: <ReceiptIcon />,
        color: 'primary',
        drawerImpact: 'لا يؤثر',
        description: 'تم استقبال الجهاز وتسجيل الطلب'
      },
      {
        date: '2024-01-22',
        action: 'إصلاح الجهاز',
        status: 'in_progress',
        icon: <BuildIcon />,
        color: 'warning',
        drawerImpact: 'لا يؤثر',
        description: 'تم تشخيص العطل وإصلاح الجهاز'
      },
      {
        date: '2024-01-27',
        action: 'تسليم الجهاز',
        status: 'delivered',
        icon: <DeliveryIcon />,
        color: 'success',
        drawerImpact: 'يؤثر على الخزينة',
        description: 'تم تسليم الجهاز للعميل'
      },
      {
        date: '2024-01-27',
        action: 'استلام الدفع',
        status: 'paid',
        icon: <PaymentIcon />,
        color: 'success',
        drawerImpact: 'يُضاف للخزينة',
        description: 'تم استلام المبلغ وإضافته للخزينة'
      }
    ]
  };

  const paymentScenarios = [
    {
      type: 'نقدي كامل',
      method: 'cash',
      amount: 700,
      cashReceived: 700,
      debt: 0,
      description: 'العميل دفع المبلغ كاملاً نقداً'
    },
    {
      type: 'دين كامل',
      method: 'debt',
      amount: 700,
      cashReceived: 0,
      debt: 700,
      description: 'العميل لم يدفع شيئاً، المبلغ كله دين'
    },
    {
      type: 'دفع جزئي',
      method: 'partial',
      amount: 700,
      cashReceived: 300,
      debt: 400,
      description: 'العميل دفع 300 نقداً والباقي 400 دين'
    }
  ];

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          📋 مثال عملي: دورة الصيانة وتأثيرها على الخزينة
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            المثال: {exampleData.device} للعميل {exampleData.customer}
          </Typography>
          <Typography variant="body2">
            رسوم الصيانة: {exampleData.maintenanceFee} جنيه + قطع غيار: {exampleData.partsRevenue} جنيه = 
            إجمالي: {exampleData.totalCost} جنيه
          </Typography>
        </Alert>

        {/* الجدول الزمني */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          🕐 الجدول الزمني للطلب
        </Typography>

        <Stepper orientation="vertical" sx={{ mt: 2 }}>
          {exampleData.timeline.map((item, index) => (
            <Step key={index} active={true} completed={true}>
              <StepLabel
                StepIconComponent={() => (
                  <Avatar
                    sx={{
                      bgcolor: item.color === 'primary' ? 'primary.main' :
                               item.color === 'warning' ? 'warning.main' :
                               item.color === 'success' ? 'success.main' : 'grey.500',
                      width: 40,
                      height: 40
                    }}
                  >
                    {item.icon}
                  </Avatar>
                )}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6">
                    {item.action}
                  </Typography>
                  <Chip
                    label={item.drawerImpact}
                    color={item.drawerImpact === 'يؤثر على الخزينة' || item.drawerImpact === 'يُضاف للخزينة' ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </StepLabel>
              <StepContent>
                <Card sx={{
                  mb: 2,
                  ml: 2,
                  bgcolor: item.drawerImpact === 'يؤثر على الخزينة' || item.drawerImpact === 'يُضاف للخزينة' ? 'success.50' : 'grey.50',
                  border: '1px solid',
                  borderColor: item.drawerImpact === 'يؤثر على الخزينة' || item.drawerImpact === 'يُضاف للخزينة' ? 'success.200' : 'grey.200'
                }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      📅 {item.date}
                    </Typography>
                    <Typography variant="body2" paragraph>
                      {item.description}
                    </Typography>
                    {(item.drawerImpact === 'يؤثر على الخزينة' || item.drawerImpact === 'يُضاف للخزينة') && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: 'success.100', borderRadius: 1 }}>
                        <Typography variant="caption" color="success.dark" fontWeight="bold">
                          💰 هذا هو اليوم الذي يؤثر على الخزينة!
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </StepContent>
            </Step>
          ))}
        </Stepper>

        {/* سيناريوهات الدفع */}
        <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
          💰 سيناريوهات الدفع المختلفة
        </Typography>

        <Grid container spacing={2}>
          {paymentScenarios.map((scenario, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    {scenario.type}
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {scenario.description}
                  </Typography>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>إجمالي المبلغ:</strong> {scenario.amount} جنيه
                    </Typography>
                    <Typography variant="body2" color="success.main">
                      <strong>نقدي للخزينة:</strong> {scenario.cashReceived} جنيه
                    </Typography>
                    <Typography variant="body2" color="warning.main">
                      <strong>دين مسجل:</strong> {scenario.debt} جنيه
                    </Typography>
                  </Box>

                  <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>تأثير على خزينة يوم 27:</strong>
                      <br />
                      + {scenario.cashReceived} جنيه نقدي
                      {scenario.debt > 0 && (
                        <>
                          <br />
                          + {scenario.debt} جنيه دين (لا يؤثر على النقدي)
                        </>
                      )}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* النقاط المهمة */}
        <Box sx={{ mt: 4, p: 3, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
          <Typography variant="h6" color="warning.main" gutterBottom>
            ⚠️ النقاط المهمة
          </Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
            <li><strong>تاريخ التأثير على الخزينة:</strong> يوم 27 (التسليم) وليس يوم 20 (الاستقبال)</li>
            <li><strong>النقدي فقط:</strong> الخزينة تتأثر بالمبلغ النقدي المستلم فعلياً</li>
            <li><strong>الديون منفصلة:</strong> الديون تُسجل في نظام منفصل ولا تؤثر على النقدي</li>
            <li><strong>الدفع الجزئي:</strong> يُقسم المبلغ بين النقدي والدين</li>
            <li><strong>تقارير الخزينة:</strong> يجب أن تعكس هذا المنطق بدقة</li>
          </Typography>
        </Box>

        {/* مثال الحساب */}
        <Box sx={{ mt: 3, p: 3, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
          <Typography variant="h6" color="success.main" gutterBottom>
            ✅ مثال الحساب الصحيح
          </Typography>
          <Typography variant="body2">
            <strong>خزينة يوم 20:</strong> 0 جنيه (لا يوجد تأثير)
            <br />
            <strong>خزينة يوم 22:</strong> 0 جنيه (لا يوجد تأثير)
            <br />
            <strong>خزينة يوم 27:</strong> المبلغ النقدي المستلم (حسب طريقة الدفع)
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default MaintenanceFlowExample;
